/* ============================================================
   Dathent — previewEntitlements.js (TASK-195, issue #314)
   The pure, testable half of PreviewContext's "is this a preview user?"
   decision. PreviewContext used to make ONE api.billing.getEntitlements()
   call on mount and freeze isPreview/locked on it. Right after a hosted
   Stripe checkout that single answer is routinely stale — the webhook that
   flips billing_status lands a few seconds after the browser comes back
   with ?checkout=success — so a user who had just PAID could be treated as
   a preview user, and if their preview budget was already spent, blurred
   on the very first render.

   RequireActiveBilling (App.jsx, handlePaid) already solves the same race
   with a poll: clear the client cache, re-read entitlements, up to 8 × 1.5 s,
   then fail open. This module mirrors that exactly, and adds one fresh
   re-check before locking on a plain mount.

   TASK-215 (issue #333, owner decision 2026-09-02): a paymentIssue
   (past_due / incomplete / unpaid subscription) no longer exempts from the
   preview lock. Those users run the SAME clock and, once it is spent, get
   the "payment failed — update your card" paywall (CTA → Plan & billing)
   instead of the subscribe one: reason 'payment_issue' on both the running
   and the locked state. RequireActiveBilling (App.jsx) still lets them past
   the card gate on purpose — that gate loops for a live past_due sub.

   Contract (never throws; every failure fails OPEN = not a preview user,
   because the blur is presentation only and the gateway keeps enforcing):
     resolvePreviewState({...}) -> { isPreview, locked, elapsed, reason }
                                 | null when isCancelled() flipped mid-way
     reason while isPreview: 'preview_running' | 'preview_spent' (subscribe
     variant) or 'payment_issue' (update-your-card variant, either state).
   ============================================================ */

import { billingIsReal } from './pendingOAuth.js';
import { freePublishingState } from './freePublishing.js';

// Same numbers as RequireActiveBilling.handlePaid in App.jsx — the webhook
// latency they were tuned for is the same webhook.
export const CHECKOUT_POLL_ATTEMPTS = 8;
export const CHECKOUT_POLL_DELAY_MS = 1500;

// "Not a preview user": an exempt org, or a billing status that grants
// access. TASK-215: a paymentIssue used to be a pass here and is not any
// more — billingStatus mirrors the Stripe subscription (past_due /
// incomplete), so billingIsReal rejects it and the user lands on the
// preview clock with the 'payment_issue' variant. Exempt still wins.
export function entitlementsAllow(ent) {
  return billingIsReal(ent) || Boolean(freePublishingState(ent));
}

// Open payment trouble on the subscription: the gateway attaches
// { status, invoiceUrl } when the latest sub is past_due / incomplete / unpaid.
export function hasPaymentIssue(ent) {
  return Boolean(ent?.paymentIssue);
}

// The preview variant for a non-allowed entitlements answer: payment trouble
// beats the plain subscribe paywall; `otherwise` is the subscribe-variant
// reason for that state ('preview_running' / 'preview_spent').
function previewReason(ent, otherwise) {
  return hasPaymentIssue(ent) ? 'payment_issue' : otherwise;
}

// The hosted-checkout return signal. Keep in sync with RequireActiveBilling
// in App.jsx (`checkout === 'success' || has('session_id')`): the success
// URLs are built in lib/plans.js and screens/Settings.jsx.
export function isCheckoutReturn(search) {
  try {
    const params = new URLSearchParams(search || '');
    return params.get('checkout') === 'success' || params.has('session_id');
  } catch (_) {
    return false;
  }
}

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Decide the preview state for a freshly mounted PreviewProvider.
 *
 * @param {object} opts
 * @param {() => Promise<object>} opts.getEntitlements  api.billing.getEntitlements
 * @param {() => void} [opts.clearCache]                api.clearCache — entitlements
 *        are cached 15 s client-side; every RE-check must bypass that cache or
 *        it would only echo the stale answer.
 * @param {boolean} [opts.checkoutReturn]  URL carries the hosted-checkout return
 * @param {number} [opts.elapsed]          active-preview seconds already spent
 * @param {number} opts.limit              preview budget (previewSeconds())
 * @param {() => boolean} [opts.isCancelled]  effect cleanup flag
 * @param {(ms:number)=>Promise<void>} [opts.sleep]
 * @param {number} [opts.attempts]
 * @param {number} [opts.delayMs]
 */
export async function resolvePreviewState({
  getEntitlements,
  clearCache = () => {},
  checkoutReturn = false,
  elapsed = 0,
  limit,
  isCancelled = () => false,
  sleep = defaultSleep,
  attempts = CHECKOUT_POLL_ATTEMPTS,
  delayMs = CHECKOUT_POLL_DELAY_MS,
}) {
  const notPreview = (reason) => ({ isPreview: false, locked: false, elapsed, reason });

  if (checkoutReturn) {
    // They just came back from Stripe: never blur, poll until the webhook
    // lands. After the budget, proceed as NOT preview — they paid; a stale
    // "none" must not put them on the clock (handlePaid fails open the same
    // way). Errors are transient here; keep polling.
    for (let i = 0; i < attempts; i += 1) {
      try {
        clearCache();
        const ent = await getEntitlements();
        if (isCancelled()) return null;
        if (entitlementsAllow(ent)) return notPreview('checkout_billing_active');
      } catch (_) {
        if (isCancelled()) return null;
      }
      if (i < attempts - 1) {
        await sleep(delayMs);
        if (isCancelled()) return null;
      }
    }
    return notPreview('checkout_poll_exhausted');
  }

  // Plain mount: one request in the common case (it rides the cached fetch
  // RequireActiveBilling just made).
  let ent;
  try {
    ent = await getEntitlements();
  } catch (_) {
    return isCancelled() ? null : notPreview('entitlements_error');
  }
  if (isCancelled()) return null;
  if (entitlementsAllow(ent)) return notPreview('billing_active');
  if (elapsed < limit) return { isPreview: true, locked: false, elapsed, reason: previewReason(ent, 'preview_running') };

  // Budget already spent → the lock would be applied synchronously on the
  // first render. Give billing one FRESH look first (cache bypassed): a user
  // who subscribed in another tab / whose webhook landed a moment ago must
  // not see the blur. Error here fails open like every other path.
  let again;
  try {
    clearCache();
    again = await getEntitlements();
    if (isCancelled()) return null;
    if (entitlementsAllow(again)) return notPreview('billing_active_on_recheck');
  } catch (_) {
    return isCancelled() ? null : notPreview('recheck_error');
  }
  // Variant from the FRESH answer — that is the one the lock is based on.
  return { isPreview: true, locked: true, elapsed, reason: previewReason(again, 'preview_spent') };
}
